import { useCallback, useRef, useState } from 'react';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { useGeminiChatsQuery } from '@anytag/graphql';
import { LIMIT } from '@anytag/shared-constants';

export const useFetchHistory = () => {
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [fetchingStatus, setFetchingStatus] = useState<'idle' | 'loading' | 'no-more'>('idle');
  const { data, fetchMore, loading } = useGeminiChatsQuery({
    variables: {
      offset: 0,
      limit: LIMIT
    }
  });

  const allGeminiChats = data?.geminiChats || [];

  const handleLoadMore = useCallback(async () => {
    if (loading || fetchingStatus !== 'idle') return;
    setFetchingStatus('loading');

    fetchMore({
      variables: {
        offset: data?.geminiChats?.length || 0,
        limit: LIMIT
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (loading) return prev;

        if (!fetchMoreResult || fetchMoreResult.geminiChats?.length === 0) {
          setFetchingStatus('no-more');
          return prev;
        }

        return {
          ...prev,
          geminiChats: [...(prev.geminiChats || []), ...(fetchMoreResult.geminiChats || [])]
        };
      }
    }).finally(() => {
      setFetchingStatus('idle');
    });
  }, [data?.geminiChats?.length, fetchMore, loading]);

  useDeepCompareEffect(() => {
    const currentRef = chatBottomRef.current;
    const currentContainer = containerRef.current;

    if (!currentRef || !currentContainer) return;

    const hasOverflow = currentContainer.scrollHeight > currentContainer.clientHeight;

    if (hasOverflow) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && fetchingStatus === 'idle') {
          handleLoadMore();
        }
      });

      if (currentRef) observer.observe(currentRef);

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }
  }, [handleLoadMore, fetchingStatus]);

  return { allGeminiChats, loading, chatBottomRef, containerRef };
};
